<template>
  <div class="session-wheel" :style="`--wheel-colour: ${wheelColour};`">
    <div
      class="container sessions-grid-layout-container"
      :class="[
        isChanging ? 'changing' : '',
        'new-session-' + (selectedSessionIndex + 1),
        'old-session-' + (newSelectedSessionIndex + 1),
        isInitializingCircle ? 'initializing-circle' : '',
      ]"
    >
      <div class="selected-session-heading">
        <div
          class="selected-session-heading-background-old"
          :style="{ backgroundImage: oldImage }"
        ></div>
        <div
          class="selected-session-heading-background-new"
          :style="{ backgroundImage: newImage }"
        ></div>
        <h1 data-test="selected-session-heading-title">{{ newSelectedSession.fullTitle }}</h1>
        <h3 class="visible-mobile">
          {{ newSelectedSession.durationDescription }}
        </h3>
        <div
          v-if="canStartSelectedSession"
          class="mobile-landscape-start-button-wrapper visible-mobile"
        >
          <a class="btn btn-primary btn-lg" :href="newSelectedSession.url" target="_self">Start</a>
        </div>
      </div>
      <div class="selected-session-info" :class="{ 'include-back-button': includeBackButton }">
        <h3 class="visible-wide">
          {{ newSelectedSession.durationDescription }}
        </h3>

        <div v-html="description"></div>
        <p v-if="newSelectedSession.isAvailable && isLastAvailableSession(newSelectedSession)">
          <b>{{ newSelectedSession.callToAction }}</b>
        </p>
      </div>
      <div class="circle-wrapper">
        <div ref="circleRef" class="circle">
          <div class="circle-background">
            <div class="circle-background-new" :style="{ backgroundImage: newImage }"></div>
            <div class="circle-background-old" :style="{ backgroundImage: oldImage }"></div>
          </div>
          <div
            ref="circleArcs"
            class="circle-arcs visible-wide"
            :class="'circle-arcs-' + sessionCount"
          >
            <div
              v-for="(session, i) in viewModel.sessions"
              class="circle-arc"
              :key="i"
              :class="session.theme + ' ' + (session.isCompleted ? 'completed' : '')"
              :data-test="`circle-arc-${i}`"
            >
              <div></div>
            </div>
          </div>
          <div class="circle-center-content visible-wide">
            <h2>{{ newSelectedSession.shortTitle }}</h2>
            <a
              v-if="canStartSelectedSession && newSelectedSession.itemTypeText === 'Step'"
              class="btn btn-lg btn-primary-forward-bold"
              :href="newSelectedSession.url"
              target="_self"
              data-test="start-session-button"
              >{{ startButtonText }}</a
            >
            <a
              v-else-if="canStartSelectedSession"
              class="btn btn-lg btn-primary"
              :href="newSelectedSession.url"
              target="_self"
              data-test="start-session-button"
              >{{ startButtonText }}</a
            >
          </div>
          <div ref="circleDragArea" class="circle-drag-area"></div>
          <div ref="circleModules" class="circle-modules">
            <div
              v-for="(session, index) in viewModel.sessions"
              class="circle-module"
              :key="index"
              @click="handleSessionClick(index)"
              :data-session-id="session.id"
              :class="[
                {
                  available: session.isAvailable,
                  completed: session.isCompleted,
                  selected: selectedSessionIndex === session.index,
                },
                session.theme,
              ]"
              :data-test="'session-' + session.id + '-module'"
            >
              <div
                :data-test="`circle-${session.id}`"
                class="circle-module-outer"
                :class="{
                  'circle-module-outer-develop': session.id === 'Develop',
                }"
              >
                <div class="circle-module-inner">
                  <span v-if="session.id !== 'Develop'">{{ itemTypeText(session) }}</span>
                  <span v-if="session.id !== 'Develop'">{{ session.index + 1 }}</span>
                  <span v-else class="develop-icon"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="mobile-portrait-start-button-wrapper visible-mobile">
        <a
          v-if="canStartSelectedSession"
          class="btn btn-primary btn-lg"
          :href="newSelectedSession.url"
          target="_self"
          data-test="start-session-button"
          >{{ startButtonText }}</a
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import cms from "../../services/cms/cms";
import { CmsRichText } from "../../services/cms/cms-type-definitions";
import { SessionApiModel, SessionsApiModel } from "../../api-models";
import { FullSessionsCircle, SessionsCircle } from "../../components/sessions/session-circle";
import MobileSessionsCircle from "../../components/sessions/mobile-session-circle";
import {
  createSessionsViewModelFromApiModel,
  SessionsViewModel,
} from "../../components/sessions/session-models";

export interface CmsSessionApiModel extends SessionApiModel {
  unavailableDescriptionFromCms: CmsRichText | undefined;
  availableDescriptionFromCms: CmsRichText | undefined;
  buttonText: string | undefined;
  itemTypeText: string | undefined;
}

const props = withDefaults(
  defineProps<{
    model: SessionsApiModel;
    wheelColour: string | null;
    includeBackButton: boolean;
  }>(),
  { wheelColour: "rgb(109, 200, 191)", includeBackButton: false },
);

const selectedSessionIndex = ref(0);
const newSelectedSessionIndex = ref(0);
const isChanging = ref(false);
const circle = ref<SessionsCircle>();
const isInitializingCircle = ref(false);
const initializingTimeout = ref<any>();

const viewModel = computed(() => {
  const model = createSessionsViewModelFromApiModel(props.model);
  return model;
});

const selectedSession = computed(
  () => viewModel.value.sessions[selectedSessionIndex.value] as CmsSessionApiModel,
);
const newSelectedSession = computed(
  () => viewModel.value.sessions[newSelectedSessionIndex.value] as CmsSessionApiModel,
);
const unavailableHtml = computed(() => {
  if (newSelectedSession.value.unavailableDescriptionFromCms) {
    return cms.asHtml(newSelectedSession.value.unavailableDescriptionFromCms);
  }
  return `<p>${(newSelectedSession.value.unavailableDescription ?? []).join("</p><p>")}</p>`;
});

function isLastAvailableSession(session: SessionApiModel): boolean {
  return session.index === viewModel.value.lastAvailableSessionIndex;
}

const availableHtml = computed(() => {
  if (newSelectedSession.value.availableDescriptionFromCms) {
    return cms.asHtml(newSelectedSession.value.availableDescriptionFromCms);
  }
  return `<p>${(newSelectedSession.value.availableDescription ?? []).join("</p><p>")}</p>`;
});

const description = computed(() => {
  let desc =
    newSelectedSession.value.unavailableDescriptionFromCms ??
    newSelectedSession.value.unavailableDescription;
  if (newSelectedSession.value.isAvailable) {
    desc =
      newSelectedSession.value.availableDescriptionFromCms ??
      newSelectedSession.value.availableDescription;
  }

  if (!desc) {
    return null;
  }

  if (typeof desc[0] === "string") {
    return `<p>${(desc as string[]).join("</p><p>")}</p>`;
  }
  return cms.asHtml(desc as any);
});

const startButtonText = computed(() => {
  if (newSelectedSession.value) {
    if (newSelectedSession.value.id === "Develop") {
      return "Start Develop";
    }

    if (newSelectedSession.value.buttonText) {
      return newSelectedSession.value.buttonText;
    }
  }

  return "Start Session";
});

const canStartSelectedSession = computed(() => {
  return newSelectedSession.value.isAvailable;
});

const sessionCount = computed(() => viewModel.value.sessions.length);

function itemTypeText(session: CmsSessionApiModel): string {
  return session.itemTypeText || "Session";
}

function getTransitionDurationInSeconds(fast: boolean): number {
  if (window.innerWidth < 1024) {
    return fast ? 0.2 : 0.3;
  }

  return fast ? 0.5 : 1;
}

function selectSession(sessionIndex: number): void {
  selectedSessionIndex.value = sessionIndex;
  isChanging.value = true;
  circle.value!.selectSession(sessionIndex, getTransitionDurationInSeconds(false)).then(() => {
    isChanging.value = false;
    newSelectedSessionIndex.value = selectedSessionIndex.value;
  });
}

function onCircleDragEnd(nearestSessionIndex: number): void {
  if (nearestSessionIndex === selectedSessionIndex.value) {
    circle.value!.selectSession(nearestSessionIndex, getTransitionDurationInSeconds(true));
  } else {
    selectSession(nearestSessionIndex);
  }
}

function getInitialOffset(windowWidth: number, windowHeight: number): number {
  if (windowWidth < 1024) {
    return windowHeight >= windowWidth ? 90 : 25;
  }

  return 360 * (-1 / viewModel.value.sessions.length); // Top -left
}

const circleRef = ref();
const circleModules = ref();
const circleDragArea = ref();
const circleArcs = ref();

function initializeCircle() {
  if (circle.value) {
    circle.value.destroy();
  }

  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;
  circle.value = circleRef.value as any;

  const circleRadiusInPixels = (circle.value as any).clientWidth / 2;
  const initialOffsetInDegrees = getInitialOffset(windowWidth, windowHeight);
  circle.value = windowWidth < 1024 ? new MobileSessionsCircle() : new FullSessionsCircle();
  circle.value.initialize(
    viewModel.value.sessions.map((s) => s.id),
    circleRadiusInPixels,
    initialOffsetInDegrees,
    circleDragArea.value as HTMLElement,
    circleArcs.value as HTMLElement,
    Array.from((circleModules.value as HTMLElement).childNodes) as HTMLElement[],
    onCircleDragEnd,
  );
  circle.value.selectSession(selectedSessionIndex.value, 0);
}

function onResize() {
  if (initializingTimeout.value) {
    clearTimeout(initializingTimeout.value);
  }

  isInitializingCircle.value = true;
  initializingTimeout.value = setTimeout(() => {
    initializeCircle();
    isInitializingCircle.value = false;
    initializingTimeout.value = undefined;
  }, 200);
}

onMounted(() => {
  selectedSessionIndex.value = viewModel.value.initialSelectedSessionIndex;
  newSelectedSessionIndex.value = selectedSessionIndex.value;

  isInitializingCircle.value = true;
  setTimeout(() => {
    initializeCircle();
    isInitializingCircle.value = false;
  });
  window.addEventListener("resize", onResize);
});

function handleSessionClick(sessionIndex: number): void {
  if (sessionIndex === selectedSessionIndex.value) {
    return;
  }

  selectSession(sessionIndex);
}

onUnmounted(() => {
  window.removeEventListener("resize", onResize);
});

const oldImage = computed(() => {
  const image = selectedSession.value.imageUrl;
  if (image) {
    return `url(${image})`;
  }

  return undefined;
});

const newImage = computed(() => {
  const image = newSelectedSession.value.imageUrl;
  if (image) {
    return `url(${image})`;
  }

  return undefined;
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
@hot-pink: #ed0b6f;
@dark-background-color: #25242e;
@circle-dropshadow-radius: 0.4rem;

@navbar-height: 50px;
@z-index-session-button: 1000 + 1; // Needs to be above the GSAP.Draggable z-index on the circle

@min-width-for-full-circle: 992px;

.circle-center-content h2 {
  text-shadow: 0px 0px 6px #000;
}
.session-1-background() {
  background-image: url(../../assets/sessions/session-splashes/session-1-splash.png);
}
.session-2-background() {
  background-image: url(../../assets/sessions/session-splashes/session-2-splash.png);
}
.session-3-background() {
  background-image: url(../../assets/sessions/session-splashes/session-3-splash.png);
}
.session-4-background() {
  background-image: url(../../assets/sessions/session-splashes/session-4-splash.png);
}
.session-5-background() {
  background-image: url(../../assets/sessions/session-splashes/session-5-splash.png);
}
.session-6-background() {
  background-image: url(../../assets/sessions/session-splashes/session-6-splash.png);
}

// Common
.circle-modules-common() {
  .circle {
    width: 2 * @circle-radius;
    height: 2 * @circle-radius;
    position: relative;
    user-select: none;
  }

  .circle-modules {
    position: absolute;
    top: 50%;
    left: 50%;
    max-width: 0;
    max-height: 0;

    .circle-module {
      position: absolute;
      left: -@module-enlarged-dimension / 2;
      top: -@module-enlarged-dimension / 2;
      width: @module-enlarged-dimension;
      height: @module-enlarged-dimension;
      cursor: pointer;

      .circle-module-outer {
        width: 100%;
        height: 100%;
        box-sizing: content-box;
        transform-origin: center;
        border-radius: 50%;
        background-color: lightgray;
        color: white;
        border: 4px solid white;
        transition: transform 0.5s;
        transform: scale(1);
      }

      &.available .circle-module-outer {
        background-color: var(--wheel-colour);

        color: white;
        border: 4px solid white;
      }

      &.locked {
        .circle-module-outer {
          color: lightgray;
        }

        .circle-module-inner {
          width: 80%;
          height: 80%;
          margin-top: 5%;
          margin-left: 10%;
          padding-top: 30%;
          background-image: url(../../assets/sessions/circle/locked-session.png);
          background-position: center center;
          background-repeat: no-repeat;
          background-size: contain;

          span:nth-child(1) {
            display: none;
          }
        }
      }

      &:hover .circle-module-outer {
        transform: scale(1.1);
      }

      &.selected .circle-module-outer {
        box-shadow: inset rgba(0, 0, 0, 0.2) 0 0 8px;
        transform: scale(1.1);
      }

      &.available:not(.completed) {
        filter: drop-shadow(0 0 @circle-dropshadow-radius rgba(255, 255, 255, 0.9));

        .circle-module-outer {
          border-color: var(--wheel-colour);
          color: var(--wheel-colour);
          background-color: white;
        }
      }

      .circle-module-inner {
        width: 100%;
        height: 100%;
        transform-origin: center;
        line-height: normal;
      }

      span {
        display: inline-block;
      }

      span:nth-child(1) {
        width: 100%;
        font-size: @module-top-font-size;
        text-align: center;
        margin-top: 1em;
      }

      span:nth-child(2) {
        width: 100%;
        font-size: @module-bottom-font-size;
        text-align: center;
      }

      span.develop-icon {
        display: inline-block !important;
        width: 100%;
        height: 100%;
        margin-top: 3%;
        background-image: url(../../assets/sessions/circle/develop-icon-for-circle-white.png);
        background-size: 70%;
        background-repeat: no-repeat;
        background-position: center center;
      }

      &.available span.develop-icon {
        background-image: url(../../assets/sessions/circle/develop-icon-for-circle-available.png);
      }

      &.completed span.develop-icon {
        background-image: url(../../assets/sessions/circle/develop-icon-for-circle-white.png);
      }

      &.locked span.develop-icon {
        background-image: url(../../assets/sessions/circle/develop-icon-for-circle-locked.png);
      }
    }
  }

  .circle-drag-area {
    position: absolute;
    top: @circle-outer-position;
    left: @circle-outer-position;
    bottom: @circle-outer-position;
    right: @circle-outer-position;
    border-radius: 50%;
  }

  .circle-background {
    position: absolute;
    top: @circle-outer-position;
    left: @circle-outer-position;
    bottom: @circle-outer-position;
    right: @circle-outer-position;
    z-index: -1;
    border-radius: 50%;
  }
}

.initializing-circle {
  .circle-modules,
  .circle-arcs {
    visibility: hidden !important;
  }
}

// Desktop/wide screen
@media (min-width: 1024px) {
  @circle-radius: 28rem;
  @module-enlarged-dimension: 10rem;
  @circle-dropshadow-radius: 0.4rem;
  @module-top-font-size: 18px;
  @module-bottom-font-size: 48px;
  @circle-full-dimension: 2 * @circle-radius + @module-enlarged-dimension * 1.2 + 3 *
    @circle-dropshadow-radius;
  @heading-min-width: 8em;
  @circle-outer-position: 0;

  .circle-transition() {
    transition: transform 2s, opacity 0.5s, left 0.2s, color 0.2s, min-width 0.5s, filter 0.5s;
  }

  .circle-transition-positional() {
    transition: transform 2s, left 0.2s, min-width 0.5s;
  }

  .circle-transition-static() {
    transition: opacity 0.5s, color 0.2s, filter 0.5s;
  }

  .sessions-grid-layout-container {
    display: grid;
    grid-gap: 10px;
    grid-template-columns: auto @circle-full-dimension;
    grid-template-rows: (
        @circle-full-dimension / 2 - @circle-radius / 2 + @module-enlarged-dimension / 2.5
      ) auto;
    grid-template-areas:
      "selected-session-heading circle"
      "selected-session circle";
  }

  .selected-session-info {
    grid-area: selected-session;
    .circle-transition-static;
    font-size: 18px;

    h3 {
      font-size: 20px;
      margin-top: 0;
      margin-bottom: 20px;
    }
  }

  .circle-wrapper {
    grid-area: circle;
    min-width: @circle-full-dimension;
    min-height: @circle-full-dimension;
    padding: (@module-enlarged-dimension * 1.2) / 2;
    overflow: hidden;
  }

  .circle {
    filter: drop-shadow(0 0 @circle-dropshadow-radius rgba(0, 0, 0, 0.3));
  }

  .selected-session-heading {
    grid-area: selected-session-heading;
    align-self: end;
    justify-self: left;

    h1 {
      position: relative;
      min-width: @heading-min-width;
      .circle-transition-static;
      font-size: 24px;
      font-weight: bold;

      &::after {
        .circle-transition-positional;
        content: "";
        border-bottom: 2px solid var(--wheel-colour);
        position: absolute;
        bottom: -6px;
        left: 0;
        right: -@circle-radius;
        z-index: -1; // Below circle
      }
    }
  }

  .selected-session-heading-background-new,
  .selected-session-heading-background-new {
    display: none;
  }

  .circle-background {
    background-color: #ffffff;

    & .circle-background-new,
    & .circle-background-old {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      border-radius: 50%;
      .circle-transition-static;
      background-position: center center;
      background-size: cover;
      opacity: 1;
    }

    .old-session-1 & .circle-background-new {
      .session-1-background();
    }
    .old-session-2 & .circle-background-new {
      .session-2-background();
    }
    .old-session-3 & .circle-background-new {
      .session-3-background();
    }
    .old-session-4 & .circle-background-new {
      .session-4-background();
    }
    .old-session-5 & .circle-background-new {
      .session-5-background();
    }
    .old-session-6 & .circle-background-new {
      .session-6-background();
    }

    .new-session-1 & .circle-background-old {
      .session-1-background();
    }
    .new-session-2 & .circle-background-old {
      .session-2-background();
    }
    .new-session-3 & .circle-background-old {
      .session-3-background();
    }
    .new-session-4 & .circle-background-old {
      .session-4-background();
    }
    .new-session-5 & .circle-background-old {
      .session-5-background();
    }
    .new-session-6 & .circle-background-old {
      .session-6-background();
    }

    .changing & .circle-background-old {
      transition: unset !important;
      opacity: 0;
    }
  }

  .nth-arc(@session-count, @ordinal) {
    @circle-session-angle: 360deg / @session-count;
    transform: rotateZ((@ordinal - 1.5) * @circle-session-angle) scale(1.01);
  }

  .circle-arcs {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    .circle-arc {
      position: absolute;
      top: 0;
      height: @circle-radius;
      overflow: hidden;
      transform-origin: bottom;

      & > div {
        width: @circle-radius * 2;
        height: @circle-radius * 2;
        border-radius: 50%;
        border: 6px solid white;
        box-sizing: border-box;
      }

      &.completed > div {
        border-color: var(--wheel-colour);
      }
    }
  }

  .circle-arcs-for-session-count(@session-count) {
    // Some of the styling of the arcs depends on how many sessions there are in the wheel
    // So we use this separate mixin to handle those rules
    @circle-session-angle: 360deg / @session-count;
    @circle-arc-width: if(
      (@session-count > 2),
      2 * @circle-radius * sin(@circle-session-angle / 2),
      2 * @circle-radius
    );

    .circle-arc {
      left: calc(50% - @circle-arc-width / 2);
      width: @circle-arc-width;

      & > div {
        margin-left: calc(-@circle-radius + @circle-arc-width / 2);
      }

      &:nth-child(1) {
        .nth-arc(@session-count, 1);
      }
      &:nth-child(2) {
        .nth-arc(@session-count, 2);
      }
      &:nth-child(3) {
        .nth-arc(@session-count, 3);
      }
      &:nth-child(4) {
        .nth-arc(@session-count, 4);
      }
      &:nth-child(5) {
        .nth-arc(@session-count, 5);
      }
      &:nth-child(6) {
        .nth-arc(@session-count, 6);
      }
      &:nth-child(7) {
        .nth-arc(@session-count, 7);
      }
      &:nth-child(8) {
        .nth-arc(@session-count, 8);
      }
      &:nth-child(9) {
        .nth-arc(@session-count, 9);
      }
      &:nth-child(10) {
        .nth-arc(@session-count, 10);
      }
      &:nth-child(11) {
        .nth-arc(@session-count, 11);
      }
      &:nth-child(12) {
        .nth-arc(@session-count, 12);
      }
    }
  }

  .circle-arcs-1 {
    .circle-arcs-for-session-count(1);

    div.circle-arc {
      // Special case for only 1 session: the arc is a full circle
      height: 2 * @circle-radius;
      top: -100%;
    }
  }

  .circle-arcs-2 {
    .circle-arcs-for-session-count(2);
  }

  .circle-arcs-3 {
    .circle-arcs-for-session-count(3);
  }

  .circle-arcs-4 {
    .circle-arcs-for-session-count(4);
  }

  .circle-arcs-5 {
    .circle-arcs-for-session-count(5);
  }

  .circle-arcs-6 {
    .circle-arcs-for-session-count(6);
  }

  .circle-arcs-7 {
    .circle-arcs-for-session-count(7);
  }

  .circle-arcs-8 {
    .circle-arcs-for-session-count(8);
  }

  .circle-arcs-9 {
    .circle-arcs-for-session-count(9);
  }

  .circle-arcs-10 {
    .circle-arcs-for-session-count(10);
  }

  .circle-arcs-11 {
    .circle-arcs-for-session-count(12);
  }

  .circle-arcs-12 {
    .circle-arcs-for-session-count(12);
  }

  .circle-center-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: @circle-radius * 2;
    height: @circle-radius * 2;
    z-index: -1;
    .circle-transition-static;

    h2 {
      text-align: center;
      font-size: 36px;
      color: white;
      max-width: calc(100% - 160px);
      margin: 20px auto 10px auto;
    }

    .btn {
      margin: 0 auto;
      z-index: @z-index-session-button;
    }

    .btn-primary-forward-bold {
      font-weight: bold;
      padding: 10px 20px;
    }
  }

  .circle-modules-common();

  .changing {
    .circle-center-content {
      opacity: 0;
    }

    .selected-session-info *,
    .selected-session-heading {
      color: white !important;
    }

    .selected-session-info * {
      opacity: 0; // Ensure links don't stay visible
    }

    .selected-session-heading h1::after {
      left: calc(100% + @circle-radius);
    }
  }

  .visible-mobile {
    display: none;
  }
}

// Mobile/small screen (COMMON)
@media (max-width: 1023px) {
  @session-heading-height: 100px;
  @content-vertical-padding: 30px;
  @content-horizontal-padding: 15px;
  @mobile-circle-background-color: rgb(228, 228, 228);

  .circle-transition() {
    transition: transform 0.2s, opacity 0.2s, color 0.2s, min-width 0.2s;
  }

  .sessions-grid-layout-container {
    padding-top: calc(@session-heading-height + @content-horizontal-padding);
  }

  .selected-session-heading {
    position: absolute;
    top: @navbar-height;
    left: 0;
    width: 100%;
    height: @session-heading-height;
    padding: 0 @content-vertical-padding;
    background-color: @dark-background-color;

    h1 {
      position: relative;
      .circle-transition;
      font-size: 120%;
      color: white;
      z-index: 2;

      &::after {
        content: "";
        width: 100%;
        border-bottom: 2px solid var(--wheel-colour);
        position: absolute;
        bottom: -6px;
        left: 0;
        z-index: 3;
      }
    }

    h3 {
      position: relative;
      .circle-transition;
      font-size: 110%;
      color: @mobile-circle-background-color;
      z-index: 2;
    }

    background-color: @dark-background-color;

    &::before,
    &::after,
    & .selected-session-heading-background-new,
    & .selected-session-heading-background-old {
      content: " ";
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      .circle-transition;
      background-position: center center;
      background-size: cover;
      filter: brightness(0.5) drop-shadow(0 0 5px rgba(0, 0, 0, 0.3));
      opacity: 1;
    }
    & .selected-session-heading-background-new,
    & .selected-session-heading-background-old {
      z-index: 1; // Above the ::before/::after
    }

    .old-session-1 &::before {
      .session-1-background();
    }
    .old-session-2 &::before {
      .session-2-background();
    }
    .old-session-3 &::before {
      .session-3-background();
    }
    .old-session-4 &::before {
      .session-4-background();
    }
    .old-session-5 &::before {
      .session-5-background();
    }
    .old-session-6 &::before {
      .session-6-background();
    }

    .new-session-1 &::after {
      .session-1-background();
    }
    .new-session-2 &::after {
      .session-2-background();
    }
    .new-session-3 &::after {
      .session-3-background();
    }
    .new-session-4 &::after {
      .session-4-background();
    }
    .new-session-5 &::after {
      .session-5-background();
    }
    .new-session-6 &::after {
      .session-6-background();
    }

    .changing &::after,
    .changing & .selected-session-heading-background-new {
      transition: unset !important;
      opacity: 0;
    }
  }

  .selected-session-info {
    .circle-transition;
    font-size: 16px;
  }

  .circle {
    .circle-transition;
  }

  .circle-background {
    filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0.3));
    background-color: @mobile-circle-background-color;
    .circle-transition;
  }

  .circle-module {
    pointer-events: none;
  }

  .changing {
    .selected-session-info,
    .selected-session-heading * {
      opacity: 0;
    }

    .selected-session-heading .selected-session-heading-background-old {
      opacity: 1;
    }

    .selected-session-heading h1::after {
      min-width: 2px;
    }

    .selected-session-heading::after {
      opacity: 0;
    }
  }

  .visible-wide {
    display: none;
  }
}

// Mobile portrait
@media (max-width: 1023px) and (orientation: portrait) {
  @circle-radius: 60vmin; // 28rem;
  @circle-height-on-screen-in-portrait: @circle-radius * 0.9;
  @module-enlarged-dimension: 22vmin;
  @session-heading-height: 100px;
  @module-outer-padding: 3vmin;
  @module-top-font-size: 4vmin;
  @module-bottom-font-size: 10vmin;
  @circle-full-dimension: 2 * @circle-radius + @module-enlarged-dimension + 2 *
    @module-outer-padding;
  @circle-outer-position: -@module-enlarged-dimension / 2 - @module-outer-padding;
  @mobile-session-start-button-wrapper-height: 60px;

  .selected-session-info {
    padding-bottom: @circle-height-on-screen-in-portrait;
  }

  .include-back-button {
    padding-top: 55px;
  }

  .circle-wrapper {
    top: calc(100% - @circle-height-on-screen-in-portrait);
    left: calc(50% - @circle-full-dimension / 2);
  }

  .circle-modules-common();

  .circle-wrapper {
    position: fixed;
    min-width: @circle-full-dimension;
    min-height: @circle-full-dimension;
    padding: @module-enlarged-dimension / 2 + @module-outer-padding;
    z-index: 1;
  }

  .mobile-portrait-start-button-wrapper {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 2;
    background-color: #404040;
    height: @mobile-session-start-button-wrapper-height;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .mobile-landscape-start-button-wrapper {
    display: none;
  }
}

// Mobile landscape
@media (max-width: 1023px) and (orientation: landscape) {
  @circle-radius: 80vh; // 28rem;
  @circle-width-on-screen-in-landscape: 40vh;
  @module-enlarged-dimension: 22vmin;
  @session-heading-height: 100px;
  @module-outer-padding: 3vmin;
  @module-top-font-size: 4vmin;
  @module-bottom-font-size: 10vmin;
  @circle-full-dimension: 2 * @circle-radius + @module-enlarged-dimension + 2 *
    @module-outer-padding;
  @circle-outer-position: -@module-enlarged-dimension / 2 - @module-outer-padding;

  .selected-session-info {
    padding-right: @circle-width-on-screen-in-landscape;
  }

  .include-back-button {
    padding-top: 55px;
  }

  .body-content {
    margin: 0; // Prevent bootstrap's auto centering for larger widths
  }

  .mobile-session-start-button-wrapper {
    display: none;
  }

  .circle-modules-common();

  .circle-wrapper {
    position: fixed;
    min-width: @circle-full-dimension;
    min-height: @circle-full-dimension;
    padding: @module-enlarged-dimension / 2 + @module-outer-padding;
    z-index: 3;
    left: calc(100% - @circle-width-on-screen-in-landscape);
    top: calc(50% - @circle-full-dimension / 2 + @navbar-height);
  }

  @start-button-left-padding: 30px;
  .mobile-landscape-start-button-wrapper {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    z-index: 2;
    left: calc(100% - 40vh - 90px - @start-button-left-padding);
    background-color: #404040;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    padding-left: @start-button-left-padding;
    border-radius: @session-heading-height / 2;
  }

  .selected-session-heading h1 {
    margin-right: calc(40vh + 90px + @start-button-left-padding);
  }

  .mobile-portrait-start-button-wrapper {
    display: none;
  }
}
</style>
